import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";
import { useToast } from "../../components/ui/Toast";
import Sidebar from "../../components/layout/Sidebar";
import VideoUploadModal from "../../components/ui/video/VideoUploadModal";

interface TyreInventoryItem {
  id: string;
  business_id: string;
  brand: string;
  model: string;
  size: string;
  type: string;
  price: number;
  quantity: number;
  stock_quantity?: number; // Alias for quantity (backwards compatibility)
  condition?: string;
  warranty_months?: number;
  installation_available?: boolean;
  installation_price?: number;
  deal_price?: number;
  deal_expires_at?: string;
  image_url?: string;
  video_url?: string | null;
  videos?: string[] | null;
  load_index?: number;
  speed_rating?: string;
  features?: any;
  created_at: string;
  updated_at: string;
}

interface RimInventoryMediaItem {
  id: string;
  brand: string;
  model: string;
  image_url: string | null;
  gallery: string[] | null;
  video_url?: string | null;
  videos?: string[] | null;
}

interface BusinessMedia {
  logoUrl: string;
  gallery: string[];
  shopVideoUrl: string;
}

type MediaKind = "image" | "video";

interface MediaItem {
  id: string;
  type: MediaKind;
  url: string;
  label: "Business" | "Tyre" | "Rim";
  title: string;
}

interface CentralTyre {
  id: string;
  brand: string;
  name: string;
  size: string;
  type: string;
  image_url?: string;
  size_code?: string;
  width?: number;
  aspect_ratio?: number;
  rim_diameter?: number;
  load_index?: number;
  speed_rating?: string;
}

const InventoryPage: React.FC = () => {
  const [inventory, setInventory] = useState<TyreInventoryItem[]>([]);
  const [rimInventoryMedia, setRimInventoryMedia] = useState<RimInventoryMediaItem[]>([]);
  const [businessMedia, setBusinessMedia] = useState<BusinessMedia>({
    logoUrl: "",
    gallery: [],
    shopVideoUrl: ""
  });
  const [centralDatabase, setCentralDatabase] = useState<CentralTyre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCentralSearch, setShowCentralSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [centralSearchTerm, setCentralSearchTerm] = useState("");
  const [qty, setQty] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedTyre, setSelectedTyre] = useState<CentralTyre | null>(null);
  const [sellerPrice, setSellerPrice] = useState<number>(0);
  const [sellerQuantity, setSellerQuantity] = useState<number>(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoTargetItem, setVideoTargetItem] = useState<TyreInventoryItem | null>(null);

  // Column filters
  const [filters, setFilters] = useState({
    brand: "",
    model: "",
    size: "",
    price: "",
    quantity: ""
  });

  // Sorting
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleFilterChange = (col: string, value: string) => {
    setFilters(prev => ({ ...prev, [col]: value }));
    setPage(1);
  };

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortOrder("asc");
    }
  };

  const resetFilters = () => {
    setFilters({
      brand: "",
      model: "",
      size: "",
      price: "",
      quantity: ""
    });
    setSearchTerm("");
    setSortBy("");
    setSortOrder("asc");
    setPage(1);
  };

  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch {
      // Optionally handle logout error
    }
  };

  useEffect(() => {
    if (!user) {
      console.error("No user found, redirecting to login");
      navigate("/login");
      return;
    }
    
    // Log user details for debugging
    console.log("User ID:", user.id);
    console.log("User email:", user.email);
    
    fetchInventory();
    fetchCentralDatabase();
  }, [user, navigate]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      
      if (!user?.id) {
        throw new Error("User ID is missing");
      }
      
      // First get the business ID for the current user
      console.log("Fetching business for user ID:", user.id);
      const { data: businessData, error: businessError } = await supabase
        .from("businesses")
        .select("id, name, verified, logo_url, gallery, shop_video_url")
        .eq("owner_id", user.id)
        .single();

      if (businessError) {
        console.error("Business fetch error:", businessError);
        throw new Error(`Failed to fetch business: ${businessError.message}`);
      }

      if (!businessData) {
        console.error("No business found for user:", user.id);
        throw new Error("No business found for this user. Please contact support.");
      }
      
      console.log("Found business:", businessData);

      setBusinessMedia({
        logoUrl: typeof businessData.logo_url === "string" ? businessData.logo_url : "",
        gallery: Array.isArray(businessData.gallery) ? businessData.gallery : [],
        shopVideoUrl: typeof businessData.shop_video_url === "string" ? businessData.shop_video_url : ""
      });

      // Fetch seller's inventory directly (no join needed)
      const { data, error } = await supabase
        .from("tyre_inventory")
        .select("*")
        .eq("business_id", businessData.id)
        .order("created_at", { ascending: false });
      
      console.log("Inventory query result:", { data, error });

      if (error) throw error;
      setInventory(data || []);

      const { data: rimData, error: rimError } = await supabase
        .from("rim_inventory")
        .select("id, brand, model, image_url, gallery, video_url, videos")
        .eq("business_id", businessData.id)
        .order("created_at", { ascending: false });

      if (rimError) {
        console.warn("Rim inventory media fetch error:", rimError);
        setRimInventoryMedia([]);
      } else {
        setRimInventoryMedia(rimData || []);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load inventory");
      showErrorToast(err.message || "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const fetchCentralDatabase = async () => {
    try {
      const { data, error } = await supabase
        .from("tyre_specifications")
        .select("*")
        .order("brand", { ascending: true });

      if (error) throw error;
      setCentralDatabase(data || []);
    } catch (err: any) {
      console.error("Failed to load central database:", err);
    }
  };

  const handleAddFromCentral = async () => {
    if (!selectedTyre || !sellerPrice || !sellerQuantity) {
      showErrorToast("Please select a tyre and enter price and quantity");
      return;
    }

    try {
      setLoading(true);
      
      if (!user?.id) {
        throw new Error("User ID is missing");
      }
      
      // Get business ID
      console.log("Adding tyre - fetching business for user ID:", user.id);
      const { data: businessData, error: businessError } = await supabase
        .from("businesses")
        .select("id, name")
        .eq("owner_id", user.id)
        .single();

      if (businessError) {
        console.error("Business fetch error:", businessError);
        throw new Error(`Failed to fetch business: ${businessError.message}`);
      }
      
      if (!businessData) {
        console.error("No business found for user:", user.id);
        throw new Error("Business not found. Please contact support.");
      }
      
      console.log("Adding tyre to business:", businessData);

      // Add tyre to seller's inventory
      const { error } = await supabase
        .from("tyre_inventory")
        .insert([{
          business_id: businessData.id,
          brand: selectedTyre.brand,
          model: selectedTyre.name,
          size: selectedTyre.size,
          type: selectedTyre.type,
          image_url: selectedTyre.image_url,
          load_index: selectedTyre.load_index,
          speed_rating: selectedTyre.speed_rating,
          price: sellerPrice,
          quantity: sellerQuantity
        }]);

      if (error) throw error;

      showSuccessToast("Tyre added to your inventory successfully");
      setSelectedTyre(null);
      setSellerPrice(0);
      setSellerQuantity(0);
      setShowCentralSearch(false);
      await fetchInventory();
    } catch (err: any) {
      showErrorToast(err.message || "Failed to add tyre to inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: TyreInventoryItem) => {
    setEditingId(item.id);
    setSellerPrice(item.price);
    setSellerQuantity(item.quantity);
  };

  const handleUpdateInventory = async () => {
    if (!editingId || !sellerPrice || !sellerQuantity) {
      showErrorToast("Please enter valid price and quantity");
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from("tyre_inventory")
        .update({
          price: sellerPrice,
          quantity: sellerQuantity
        })
        .eq("id", editingId);

      if (error) throw error;

      showSuccessToast("Inventory updated successfully");
      setEditingId(null);
      setSellerPrice(0);
      setSellerQuantity(0);
      fetchInventory();
    } catch (err: any) {
      showErrorToast(err.message || "Failed to update inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to remove this tyre from your inventory?")) {
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from("tyre_inventory")
        .delete()
        .eq("id", id);

      if (error) throw error;

      showSuccessToast("Tyre removed from inventory");
      fetchInventory();
    } catch (err: any) {
      showErrorToast(err.message || "Failed to remove tyre");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenVideoModal = (item: TyreInventoryItem) => {
    setVideoTargetItem(item);
    setVideoModalOpen(true);
  };

  const handleSaveTyreVideos = async (
    videoUrl: string | null,
    _videoType?: string,
    additionalVideos?: string[]
  ) => {
    if (!videoTargetItem) {
      throw new Error("No inventory item selected for video upload");
    }

    const normalizeUniqueUrls = (urls: Array<string | null | undefined>) => {
      const seen = new Set<string>();
      const result: string[] = [];

      urls.forEach((url) => {
        if (typeof url !== "string") {
          return;
        }

        const trimmed = url.trim();
        if (!trimmed || seen.has(trimmed)) {
          return;
        }

        seen.add(trimmed);
        result.push(trimmed);
      });

      return result;
    };

    let normalizedPrimary = typeof videoUrl === "string" && videoUrl.trim().length > 0
      ? videoUrl.trim()
      : null;
    let normalizedAdditional = normalizeUniqueUrls(additionalVideos ?? []);

    if (normalizedPrimary) {
      normalizedAdditional = normalizedAdditional.filter((url) => url !== normalizedPrimary);
    }

    if (!normalizedPrimary && normalizedAdditional.length > 0) {
      normalizedPrimary = normalizedAdditional[0];
      normalizedAdditional = normalizedAdditional.slice(1);
    }

    const { error } = await supabase
      .from("tyre_inventory")
      .update({
        video_url: normalizedPrimary,
        videos: normalizedAdditional
      })
      .eq("id", videoTargetItem.id);

    if (error) {
      throw error;
    }

    showSuccessToast("Video saved to tyre listing");
    setVideoModalOpen(false);
    setVideoTargetItem(null);
    await fetchInventory();
  };

  // Filter central database tyres
  const filteredCentralTyres = centralDatabase.filter(tyre =>
    tyre.brand?.toLowerCase().includes(centralSearchTerm.toLowerCase()) ||
    tyre.name?.toLowerCase().includes(centralSearchTerm.toLowerCase()) ||
    tyre.size?.toLowerCase().includes(centralSearchTerm.toLowerCase()) ||
    tyre.type?.toLowerCase().includes(centralSearchTerm.toLowerCase())
  );

  const filteredInventory = inventory.filter(item =>
    (item.brand || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.model || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.size || "").toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(item =>
    (!filters.brand || (item.brand || "").toLowerCase().includes(filters.brand.toLowerCase())) &&
    (!filters.model || (item.model || "").toLowerCase().includes(filters.model.toLowerCase())) &&
    (!filters.size || (item.size || "").toLowerCase().includes(filters.size.toLowerCase())) &&
    (!filters.price || item.price.toString().includes(filters.price)) &&
    (!filters.quantity || item.quantity.toString().includes(filters.quantity))
  );
  
  const sortedInventory = sortBy
    ? [...filteredInventory].sort((a, b) => {
        let aVal: any, bVal: any;
        
        if (sortBy === "price" || sortBy === "quantity") {
          aVal = a[sortBy as keyof TyreInventoryItem];
          bVal = b[sortBy as keyof TyreInventoryItem];
        } else if (sortBy === "brand") {
          aVal = a.brand || "";
          bVal = b.brand || "";
        } else if (sortBy === "name") {
          aVal = a.model || "";
          bVal = b.model || "";
        } else if (sortBy === "size") {
          aVal = a.size || "";
          bVal = b.size || "";
        } else if (sortBy === "type") {
          aVal = a.type || "";
          bVal = b.type || "";
        } else {
          aVal = a[sortBy as keyof TyreInventoryItem] || "";
          bVal = b[sortBy as keyof TyreInventoryItem] || "";
        }
        
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
        }
        return sortOrder === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      })
    : filteredInventory;
  
  const paginatedInventory = sortedInventory.slice((page - 1) * qty, page * qty);
  const totalPages = Math.ceil(sortedInventory.length / qty);

  const mediaItems = useMemo(() => {
    const items: MediaItem[] = [];
    const seen = new Set<string>();

    const pushItem = (item: MediaItem) => {
      if (!item.url || seen.has(item.url)) {
        return;
      }
      seen.add(item.url);
      items.push(item);
    };

    if (businessMedia.logoUrl) {
      pushItem({
        id: "business-logo",
        type: "image",
        url: businessMedia.logoUrl,
        label: "Business",
        title: "Logo"
      });
    }

    businessMedia.gallery.forEach((url, index) => {
      pushItem({
        id: `business-gallery-${index}`,
        type: "image",
        url,
        label: "Business",
        title: `Gallery ${index + 1}`
      });
    });

    if (businessMedia.shopVideoUrl) {
      pushItem({
        id: "business-video",
        type: "video",
        url: businessMedia.shopVideoUrl,
        label: "Business",
        title: "Shop video"
      });
    }

    inventory.forEach((item) => {
      const title = [item.brand, item.model].filter(Boolean).join(" ").trim();
      if (item.image_url) {
        pushItem({
          id: `tyre-image-${item.id}`,
          type: "image",
          url: item.image_url,
          label: "Tyre",
          title
        });
      }

      const tyreVideos = [item.video_url, ...(item.videos ?? [])]
        .filter((url): url is string => typeof url === "string" && url.length > 0);

      tyreVideos.forEach((url, index) => {
        pushItem({
          id: `tyre-video-${item.id}-${index}`,
          type: "video",
          url,
          label: "Tyre",
          title
        });
      });
    });

    rimInventoryMedia.forEach((rim) => {
      const title = [rim.brand, rim.model].filter(Boolean).join(" ").trim();

      if (rim.image_url) {
        pushItem({
          id: `rim-image-${rim.id}`,
          type: "image",
          url: rim.image_url,
          label: "Rim",
          title
        });
      }

      (rim.gallery ?? []).forEach((url, index) => {
        pushItem({
          id: `rim-gallery-${rim.id}-${index}`,
          type: "image",
          url,
          label: "Rim",
          title
        });
      });

      const rimVideos = [rim.video_url, ...(rim.videos ?? [])]
        .filter((url): url is string => typeof url === "string" && url.length > 0);

      rimVideos.forEach((url, index) => {
        pushItem({
          id: `rim-video-${rim.id}-${index}`,
          type: "video",
          url,
          label: "Rim",
          title
        });
      });
    });

    return items;
  }, [businessMedia, inventory, rimInventoryMedia]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading inventory...</p>
          {user?.email && (
            <p className="mt-1 text-xs text-gray-500">Logged in as: {user.email}</p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Inventory</h2>
            <p className="text-red-600 mb-4">{error}</p>
            {user?.email && (
              <p className="text-sm text-red-600 mb-2">User: {user.email}</p>
            )}
            <div className="space-y-2">
              <button
                onClick={fetchInventory}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 mr-2"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate("/business/seller-dashboard")}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            <p>If this problem persists:</p>
            <ul className="mt-2 text-left space-y-1">
              <li>• Check that your business is properly registered</li>
              <li>• Contact support if you need assistance</li>
              <li>• Try logging out and logging back in</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar Navigation */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {user?.email}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-6">
            <button
              onClick={() => setShowCentralSearch(!showCentralSearch)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              {showCentralSearch ? 'Cancel' : 'Add from Central Database'}
            </button>
          </div>

          {/* Search and Controls */}
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <input
              type="text"
              placeholder="Search by brand, model, or size..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 max-w-md border border-gray-300 rounded-lg px-4 py-2"
            />
            <button
              onClick={resetFilters}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Reset Filters
            </button>
          </div>

          {/* Qty Dropdown for row filtering */}
          <div className="mb-6 flex items-center gap-4">
            <label htmlFor="qty" className="text-sm font-medium">Rows per page:</label>
            <select
              id="qty"
              value={qty}
              onChange={e => setQty(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-1"
              style={{ width: 80 }}
            >
              {[5, 10, 20, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          {/* Media Library */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Inventory Media Library</h2>
              <span className="text-sm text-gray-500">{mediaItems.length} items</span>
            </div>

            {mediaItems.length === 0 ? (
              <p className="text-sm text-gray-500">
                No media found yet. Add images or videos to your business, tyre, or rim listings.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {mediaItems.map((media) => (
                  <div
                    key={media.id}
                    className="group overflow-hidden rounded-lg border border-gray-200 bg-white"
                  >
                    <div className="relative">
                      {media.type === "image" ? (
                        <img
                          src={media.url}
                          alt={media.title || "Inventory media"}
                          className="h-28 w-full object-cover bg-gray-100"
                          loading="lazy"
                        />
                      ) : (
                        <video
                          src={media.url}
                          className="h-28 w-full object-cover bg-gray-100"
                          preload="metadata"
                          controls
                        />
                      )}
                      <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {media.label}
                      </span>
                    </div>
                    <div className="px-2 py-2">
                      <div className="text-xs text-gray-500 truncate">
                        {media.title || "Untitled"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Central Database Search */}
          {showCentralSearch && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm p-6 mb-6"
            >
              <h3 className="text-lg font-semibold mb-4">Search Central Tyre Database</h3>
              <p className="text-sm text-gray-600 mb-4">
                Select tyres from the central database and add your pricing and stock quantity.
              </p>
              
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by brand, model, size, or type..."
                  value={centralSearchTerm}
                  onChange={(e) => setCentralSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                />
              </div>

              {/* Central Database Results */}
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCentralTyres.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">
                          No tyres found. Try adjusting your search.
                        </td>
                      </tr>
                    ) : (
                      filteredCentralTyres.map((tyre) => (
                        <tr key={tyre.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="radio"
                              name="selectedTyre"
                              onChange={() => setSelectedTyre(tyre)}
                              className="text-blue-600"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {tyre.brand}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tyre.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tyre.size}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              tyre.type ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {tyre.type || 'Not specified'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Add Selected Tyre Form */}
              {selectedTyre && (
                <div className="mt-6 p-4 border rounded-lg bg-blue-50">
                  <h4 className="font-semibold mb-4">
                    Add to Your Inventory: {selectedTyre.brand} {selectedTyre.name} ({selectedTyre.size})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Your Price (R)</label>
                      <input
                        type="number"
                        value={sellerPrice || ''}
                        onChange={(e) => setSellerPrice(Number(e.target.value))}
                        className="w-full border rounded px-3 py-2"
                        step="0.01"
                        placeholder="e.g. 1500.00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stock Quantity</label>
                      <input
                        type="number"
                        value={sellerQuantity || ''}
                        onChange={(e) => setSellerQuantity(Number(e.target.value))}
                        className="w-full border rounded px-3 py-2"
                        placeholder="e.g. 20"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleAddFromCentral}
                      disabled={loading || !sellerPrice || !sellerQuantity}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Add to My Inventory
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTyre(null);
                        setSellerPrice(0);
                        setSellerQuantity(0);
                      }}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowCentralSearch(false);
                    setSelectedTyre(null);
                    setSellerPrice(0);
                    setSellerQuantity(0);
                    setCentralSearchTerm("");
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </motion.div>
          )}

          {/* Edit Inventory Item */}
          {editingId && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm p-6 mb-6"
            >
              <h3 className="text-lg font-semibold mb-4">Update Inventory Item</h3>
              <p className="text-sm text-gray-600 mb-4">
                You can only update the price and stock quantity for this tyre.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Your Price (R)</label>
                  <input
                    type="number"
                    value={sellerPrice || ''}
                    onChange={(e) => setSellerPrice(Number(e.target.value))}
                    className="w-full border rounded px-3 py-2"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stock Quantity</label>
                  <input
                    type="number"
                    value={sellerQuantity || ''}
                    onChange={(e) => setSellerQuantity(Number(e.target.value))}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleUpdateInventory}
                  disabled={loading || !sellerPrice || !sellerQuantity}
                  className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50"
                >
                  Update Inventory
                </button>
                <button
                  onClick={() => {
                    setEditingId(null);
                    setSellerPrice(0);
                    setSellerQuantity(0);
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          {/* Inventory Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                Current Inventory ({filteredInventory.length} items)
              </h3>
            </div>
            
            {filteredInventory.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p>No tyres in inventory.</p>
                <button
                  onClick={() => setShowCentralSearch(true)}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  Add your first tyre from central database
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("brand")}>
                        Brand
                        {sortBy === "brand" && (sortOrder === "asc" ? " ▲" : " ▼")}
                        <input
                          type="text"
                          value={filters.brand}
                          onChange={e => handleFilterChange("brand", e.target.value)}
                          placeholder="Filter"
                          className="mt-1 w-full border rounded px-2 py-1 text-xs"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("name")}>
                        Model
                        {sortBy === "name" && (sortOrder === "asc" ? " ▲" : " ▼")}
                        <input
                          type="text"
                          value={filters.model}
                          onChange={e => handleFilterChange("model", e.target.value)}
                          placeholder="Filter"
                          className="mt-1 w-full border rounded px-2 py-1 text-xs"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("size")}>
                        Size
                        {sortBy === "size" && (sortOrder === "asc" ? " ▲" : " ▼")}
                        <input
                          type="text"
                          value={filters.size}
                          onChange={e => handleFilterChange("size", e.target.value)}
                          placeholder="Filter"
                          className="mt-1 w-full border rounded px-2 py-1 text-xs"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("type")}>
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("price")}>
                        Price
                        {sortBy === "price" && (sortOrder === "asc" ? " ▲" : " ▼")}
                        <input
                          type="text"
                          value={filters.price}
                          onChange={e => handleFilterChange("price", e.target.value)}
                          placeholder="Filter"
                          className="mt-1 w-full border rounded px-2 py-1 text-xs"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("quantity")}>
                        Stock
                        {sortBy === "quantity" && (sortOrder === "asc" ? " ▲" : " ▼")}
                        <input
                          type="text"
                          value={filters.quantity}
                          onChange={e => handleFilterChange("quantity", e.target.value)}
                          placeholder="Filter"
                          className="mt-1 w-full border rounded px-2 py-1 text-xs"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedInventory.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.image_url ? (
                            <img src={item.image_url} alt="Tyre" className="h-10 w-10 object-cover rounded" />
                          ) : (
                            <span className="text-xs text-gray-400">No image</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.video_url || (item.videos && item.videos.length > 0) ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 font-medium">
                              Video added
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No video</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.brand}</div>
                          <div className="text-xs text-gray-500">Your inventory</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{item.model}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.size}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 capitalize">{item.type}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium text-green-600">R {item.price.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">Your price</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.quantity > 10
                              ? 'bg-green-100 text-green-800'
                              : item.quantity > 0
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {item.quantity} units
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-xs text-gray-400">Direct Entry</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit Price/Qty
                          </button>
                          <button
                            onClick={() => handleOpenVideoModal(item)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Manage Video
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t">
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <VideoUploadModal
        isOpen={videoModalOpen}
        onClose={() => {
          setVideoModalOpen(false);
          setVideoTargetItem(null);
        }}
        onSubmit={handleSaveTyreVideos}
        productName={videoTargetItem ? `${videoTargetItem.brand} ${videoTargetItem.model}` : "Tyre"}
        existingPrimaryVideoUrl={videoTargetItem?.video_url ?? null}
        existingAdditionalVideos={videoTargetItem?.videos ?? []}
        allowMultiple={true}
        maxVideos={5}
      />
    </div>
  );
};

export default InventoryPage;